/**
 * TanStack Query Hook for Active Unicorns KPI
 *
 * This hook provides access to the active unicorns KPI data from the database.
 * It uses TanStack Query for efficient data fetching with automatic caching,
 * background refetching, and error handling.
 *
 * Database Source: web.v_kpi_unicorns_active_total_v1 view
 *
 * @module useKpiUnicornsActiveTotalV1
 * @category Database Hooks
 *
 * @example
 * ```typescript
 * // Basic usage in a component
 * import { useKpiUnicornsActiveTotalV1 } from '@/hooks/database/useKpiUnicornsActiveTotalV1';
 *
 * function ActiveUnicornsDisplay() {
 *   const { data, isLoading, error } = useKpiUnicornsActiveTotalV1();
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (error) return <div>Error: {error.message}</div>;
 *
 *   return (
 *     <div>
 *       <h2>Active Unicorns</h2>
 *       <p>Count: {data.unicorn_count}</p>
 *       <p>Last Updated: {new Date(data.calculation_date).toLocaleDateString()}</p>
 *     </div>
 *   );
 * }
 * ```
 *
 * @example
 * ```typescript
 * // Advanced usage with refetch control
 * function KpiDashboard() {
 *   const { data, isLoading, error, refetch, isFetching } = useKpiUnicornsActiveTotalV1();
 *
 *   const handleRefresh = () => {
 *     refetch();
 *   };
 *
 *   return (
 *     <div>
 *       <button onClick={handleRefresh} disabled={isFetching}>
 *         {isFetching ? 'Refreshing...' : 'Refresh Data'}
 *       </button>
 *       {data && <KpiCard kpi={data} />}
 *     </div>
 *   );
 * }
 * ```
 */

import { useQuery, UseQueryResult, QueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { KpiUnicornsActiveTotal } from '@/types/kpi.types';

/**
 * Fetches active unicorns KPI data from the database
 *
 * This function queries the web.v_kpi_unicorns_active_total_v1 view and returns
 * a single row with the current active unicorns count.
 *
 * @returns {Promise<KpiUnicornsActiveTotal>} KPI data object with unicorn count and metadata
 * @throws {Error} If the database query fails or returns invalid data
 *
 * @internal
 */
const fetchKpiUnicornsActiveTotal = async (): Promise<KpiUnicornsActiveTotal> => {
  const { data, error } = await supabase
    .schema('web')
    .from('v_kpi_unicorns_active_total_v1')
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to fetch active unicorns KPI: ${error.message}`);
  }

  if (!data) {
    throw new Error('No data returned from active unicorns KPI query');
  }

  return data;
};

/**
 * React Hook for Active Unicorns KPI Data
 *
 * Fetches and caches the active unicorns KPI data using TanStack Query.
 * The data is cached for 1 hour and will not automatically refetch on
 * window focus or component mount.
 *
 * Query Configuration:
 * - Query Key: ['kpi', 'unicorns-active-total', 'v1']
 * - Stale Time: 1 hour (3600000ms)
 * - Refetch on Window Focus: false
 * - Refetch on Mount: false
 * - Retry Attempts: 2
 *
 * @returns {UseQueryResult<KpiUnicornsActiveTotal>} TanStack Query result object
 * @property {KpiUnicornsActiveTotal | undefined} data - The KPI data (undefined during loading)
 * @property {boolean} isLoading - True if query is in initial loading state
 * @property {boolean} isFetching - True if query is fetching (including background fetches)
 * @property {boolean} isError - True if query encountered an error
 * @property {Error | null} error - The error object if query failed
 * @property {Function} refetch - Function to manually refetch the data
 *
 * @example
 * ```typescript
 * // Simple usage
 * const { data, isLoading } = useKpiUnicornsActiveTotalV1();
 * console.log(data?.unicorn_count); // e.g., 8
 * ```
 *
 * @example
 * ```typescript
 * // Error handling
 * const { data, isLoading, isError, error } = useKpiUnicornsActiveTotalV1();
 *
 * if (isLoading) {
 *   return <Spinner />;
 * }
 *
 * if (isError) {
 *   return <ErrorAlert message={error?.message} />;
 * }
 *
 * return <KpiDisplay value={data.unicorn_count} />;
 * ```
 *
 * @example
 * ```typescript
 * // Manual refetch
 * const { data, refetch } = useKpiUnicornsActiveTotalV1();
 *
 * const handleUpdate = async () => {
 *   await refetch();
 *   toast.success('KPI data refreshed');
 * };
 * ```
 */
export const useKpiUnicornsActiveTotalV1 = (): UseQueryResult<KpiUnicornsActiveTotal> => {
  return useQuery<KpiUnicornsActiveTotal>({
    queryKey: ['kpi', 'unicorns-active-total', 'v1'],
    queryFn: fetchKpiUnicornsActiveTotal,
    staleTime: 3600000, // 1 hour in milliseconds
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
  });
};

/**
 * Query Key Factory for Active Unicorns KPI
 *
 * Provides consistent query key generation for TanStack Query.
 * Useful for manual cache manipulation or query invalidation.
 *
 * @returns {string[]} The query key array
 *
 * @example
 * ```typescript
 * import { useQueryClient } from '@tanstack/react-query';
 * import { getKpiUnicornsActiveTotalQueryKey } from '@/hooks/database/useKpiUnicornsActiveTotalV1';
 *
 * function RefreshButton() {
 *   const queryClient = useQueryClient();
 *
 *   const invalidateCache = () => {
 *     queryClient.invalidateQueries({
 *       queryKey: getKpiUnicornsActiveTotalQueryKey()
 *     });
 *   };
 *
 *   return <button onClick={invalidateCache}>Clear Cache</button>;
 * }
 * ```
 */
export const getKpiUnicornsActiveTotalQueryKey = (): string[] => {
  return ['kpi', 'unicorns-active-total', 'v1'];
};

/**
 * Prefetch Function for Active Unicorns KPI
 *
 * Allows prefetching the KPI data before component mount.
 * Useful for optimistic data loading and improving perceived performance.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient - TanStack Query client instance
 * @returns {Promise<void>}
 *
 * @example
 * ```typescript
 * import { useQueryClient } from '@tanstack/react-query';
 * import { prefetchKpiUnicornsActiveTotal } from '@/hooks/database/useKpiUnicornsActiveTotalV1';
 *
 * function App() {
 *   const queryClient = useQueryClient();
 *
 *   useEffect(() => {
 *     // Prefetch KPI data on app load
 *     prefetchKpiUnicornsActiveTotal(queryClient);
 *   }, [queryClient]);
 *
 *   return <Dashboard />;
 * }
 * ```
 */
export const prefetchKpiUnicornsActiveTotal = async (
  queryClient: QueryClient
): Promise<void> => {
  await queryClient.prefetchQuery({
    queryKey: getKpiUnicornsActiveTotalQueryKey(),
    queryFn: fetchKpiUnicornsActiveTotal,
    staleTime: 3600000,
  });
};
